import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { COACHING_FEELING_LABELS, moodBand, moodCoverage } from '@mlm/shared/coaching';
import { Block, Chip, ErrorNote, Eyebrow, Loading, Screen } from '../components/primitives';
import { coaching, type MoodDay } from '../lib/api';
import { longDay, shortDay } from '../lib/format';
import { qk } from '../lib/queries';

const RANGES = [7, 30, 90] as const;

/**
 * SC-38's mood dimension over time.
 *
 * There is no question on this screen. The mood it draws is the one the
 * evening review already asks for, so a member answers once a day and this is
 * the record of those answers. A day with no review is a gap, not a low score.
 */
export function MoodPage() {
  const [days, setDays] = useState<number>(30);
  const mood = useQuery({ queryKey: qk.mood(days), queryFn: () => coaching.mood(days) });

  if (mood.isPending) return <Loading />;
  if (mood.isError) return <ErrorNote error={mood.error} retry={() => void mood.refetch()} />;
  const m = mood.data;

  return (
    <Screen
      back="/achieve"
      eyebrow="Mood"
      title={m.average === null ? 'No mood yet' : moodBand(m.average)}
      subtitle={
        m.average === null
          ? 'Your mood comes from the evening review. Close a day and it starts here.'
          : `Averaging ${m.average.toFixed(1)} of 5 across ${moodCoverage(m.recorded, days).toLowerCase()}.`
      }
    >
      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-3">Range</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <Chip key={r} selected={days === r} onClick={() => setDays(r)}>
              {r} days
            </Chip>
          ))}
        </div>
      </section>

      <MoodChart days={m.days} />

      {m.commonFeeling && (
        <Block eyebrow="Most often">
          You chose “{COACHING_FEELING_LABELS[m.commonFeeling]}” more than any other feeling in this
          window.
        </Block>
      )}

      {m.recorded === 0 && (
        <Block
          action={
            <Link
              to="/review/evening"
              className="eyebrow underline underline-offset-4 hover:text-ink"
            >
              Close today
            </Link>
          }
        >
          Nothing to chart yet. The evening review asks how the day felt, and each answer becomes a
          bar here.
        </Block>
      )}

      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        This is a record of what you reported, not a measurement of how you are. A gap is a day you
        did not close, not a bad one.
      </p>
    </Screen>
  );
}

/**
 * One bar a day, height from the mood. Bars are one colour: the height already
 * carries the value, and a five-step colour ramp would put four new hues on a
 * screen the palette keeps near-monochrome. A day with no review draws the
 * baseline hairline instead of a bar, so a gap can never read as a low day.
 */
function MoodChart({ days }: { days: MoodDay[] }) {
  // Past about six weeks the per-day labels stop being readable, so only the
  // ends are named and the bars get thin enough to fit.
  const dense = days.length > 31;
  return (
    <section className="border-b border-border px-5 py-6">
      <Eyebrow className="mb-4">Each evening you closed</Eyebrow>
      <div className="flex h-32 items-end gap-px" role="img" aria-label={chartLabel(days)}>
        {days.map((d) => (
          <div key={d.day} className="flex h-full flex-1 items-end">
            {d.mood === null ? (
              <div className="h-px w-full bg-[rgba(16,32,26,0.14)]" />
            ) : (
              <div
                className="w-full bg-verde"
                style={{ height: `${(d.mood / 5) * 100}%` }}
                title={`${longDay(d.day)}: ${moodBand(d.mood)}${d.feeling ? ` (${COACHING_FEELING_LABELS[d.feeling]})` : ''}`}
              />
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-between font-mono text-[0.625rem] uppercase tracking-[0.12em] text-sage-deep">
        <span>{shortDay(days[0]!.day)}</span>
        {!dense && <span>{shortDay(days[Math.floor(days.length / 2)]!.day)}</span>}
        <span>Today</span>
      </div>
    </section>
  );
}

function chartLabel(days: MoodDay[]): string {
  const recorded = days.filter((d) => d.mood !== null);
  if (recorded.length === 0) return `No mood recorded in the last ${days.length} days`;
  return `Mood on ${recorded.length} of the last ${days.length} days, from ${shortDay(days[0]!.day)} to today`;
}
